import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, AlertTriangle, CalendarClock, UserPlus, Receipt, CheckCheck, FileUp, MessageSquarePlus, MessageCircle, FileSignature } from 'lucide-react';
import { Button } from './ui/button';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { notificationsApi } from '../api';

const TYPE_META = {
  overdue: { icon: AlertTriangle, cls: 'bg-red-50 text-red-700', label: 'Overdue' },
  due_soon: { icon: CalendarClock, cls: 'bg-amber-50 text-amber-700', label: 'Due soon' },
  assigned: { icon: UserPlus, cls: 'bg-sky-50 text-sky-700', label: 'New task' },
  invoice_overdue: { icon: Receipt, cls: 'bg-red-50 text-red-700', label: 'Invoice' },
  upload: { icon: FileUp, cls: 'bg-emerald-50 text-emerald-700', label: 'Client upload' },
  bk_approval: { icon: CheckCheck, cls: 'bg-violet-50 text-violet-700', label: 'Bookkeeping approval' },
  portal_request: { icon: MessageSquarePlus, cls: 'bg-sky-50 text-sky-700', label: 'Portal request' },
  portal_message: { icon: MessageCircle, cls: 'bg-sky-50 text-sky-700', label: 'Client reply' },
  portal_signoff: { icon: FileSignature, cls: 'bg-emerald-50 text-emerald-700', label: 'Sign-off' },
};

const when = (iso, type) => {
  if (!iso) return '';
  const d = new Date(iso);
  const days = Math.round((d - Date.now()) / 86400000);
  if (type === 'due_soon') return days <= 0 ? 'Due today' : `Due in ${days} day${days === 1 ? '' : 's'}`;
  if (type === 'overdue') return `${Math.max(1, -days)} day${-days === 1 ? '' : 's'} overdue`;
  return d.toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
};

const Item = ({ n, onClick }) => {
  const m = TYPE_META[n.type] || TYPE_META.assigned;
  return (
    <button type="button" onClick={onClick} className={`w-full text-left px-4 py-3 flex gap-3 hover:bg-stone-50 transition-colors ${n.read ? 'opacity-60' : ''}`} data-testid={`notification-item-${n.id}`}>
      <div className={`h-8 w-8 rounded-lg grid place-items-center flex-shrink-0 ${m.cls}`}><m.icon size={15}/></div>
      <div className="min-w-0 flex-1">
        <div className="text-sm font-medium text-stone-900 truncate">{n.title}</div>
        <div className="text-xs text-stone-500 truncate">{n.subtitle}</div>
        <div className="text-[11px] text-stone-400 mt-0.5">{m.label} · {when(n.at, n.type)}</div>
      </div>
      {!n.read && <span className="mt-1.5 h-2 w-2 rounded-full bg-emerald-600 flex-shrink-0"/>}
    </button>
  );
};

export const NotificationBell = () => {
  const nav = useNavigate();
  const [data, setData] = useState({ items: [], unread: 0 });
  const [open, setOpen] = useState(false);

  const load = useCallback(() => notificationsApi.list().then(setData).catch(() => {}), []);
  useEffect(() => { load(); const iv = setInterval(load, 60000); return () => clearInterval(iv); }, [load]);
  useEffect(() => { if (open) load(); }, [open, load]);

  const markRead = async (ids) => {
    if (!ids.length) return;
    setData((d) => ({ items: d.items.map((i) => (ids.includes(i.id) ? { ...i, read: true } : i)), unread: d.items.filter((i) => !i.read && !ids.includes(i.id)).length }));
    await notificationsApi.markRead(ids).catch(() => {});
  };

  const openItem = (n) => { markRead([n.id]); setOpen(false); nav(n.link); };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="icon" className="relative" data-testid="notification-bell-btn">
          <Bell size={18}/>
          {data.unread > 0 && (
            <span className="absolute top-1 right-1 min-w-[16px] h-4 px-1 rounded-full bg-red-500 text-white text-[10px] font-bold grid place-items-center" data-testid="notification-unread-count">
              {data.unread > 99 ? '99+' : data.unread}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-96 p-0" data-testid="notification-panel">
        <div className="px-4 py-3 border-b border-stone-100 flex items-center justify-between">
          <div className="font-semibold text-stone-900 text-sm">Notifications</div>
          <button type="button" onClick={() => markRead(data.items.filter((i) => !i.read).map((i) => i.id))} disabled={!data.unread} className="text-xs text-emerald-800 hover:underline disabled:opacity-40 disabled:no-underline inline-flex items-center gap-1" data-testid="notification-mark-all-read-btn">
            <CheckCheck size={13}/> Mark all read
          </button>
        </div>
        <div className="max-h-[60vh] overflow-y-auto divide-y divide-stone-100">
          {data.items.length === 0 && <div className="p-6 text-sm text-stone-500 text-center" data-testid="notification-empty">You&apos;re all caught up.</div>}
          {data.items.map((n) => <Item key={n.id} n={n} onClick={() => openItem(n)}/>)}
        </div>
      </PopoverContent>
    </Popover>
  );
};
